import { GraphQLFormattedError } from 'graphql';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';

import { useViewerRoles } from '@treams/treams-hooks';
import { ArchiveIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Callout,
	Card,
	ClipboardButton,
	Container,
	GraphQLErrorMessage,
	Heading,
	Heading3,
	Input,
	Label,
	Paragraph,
	Separator,
	Spinner,
	useToasts,
} from '@treams/treams-ui';

import ProductTile from '../ProductTile';
import PermissionsTree from './graphql-api/PermissionsTree';

const LOGO_URL = 'https://assets.ontreams.com/marketplace/graphql.png';

type Credentials = { apiKey: string; apiSecret: string };

/** API users are managed by workspace superusers only (`ApiUser.manage`). */
export function useCanManageApiUsers(): { canManage: boolean; loading: boolean } {
	const { loading, isWorkspaceSuperuser } = useViewerRoles();
	return { canManage: isWorkspaceSuperuser, loading };
}

export default function GraphqlApi() {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const { canManage, loading: accessLoading } = useCanManageApiUsers();

	const query = graphql.useApiUsersQuery({
		fetchPolicy: 'cache-and-network',
		skip: accessLoading || !canManage,
	});

	const [create, { loading: creating }] = graphql.useCreateApiUserMutation();
	const [regenerate] = graphql.useRegenerateApiUserSecretMutation();
	const [archive] = graphql.useArchiveApiUserMutation();
	const [updatePermissions] = graphql.useUpdateApiUserPermissionsMutation();

	const [credentials, setCredentials] = useState<Credentials | undefined>(undefined);
	const [error, setError] = useState<GraphQLFormattedError | undefined>(undefined);

	const run = async (action: () => Promise<unknown>) => {
		setError(undefined);
		try {
			await action();
		} catch (err) {
			setError(
				err.graphQLErrors?.[0] ?? { extensions: { error: { key: 'common.errors.offline' } } },
			);
		}
	};

	if (accessLoading) {
		return null;
	}

	if (!canManage) {
		return <Redirect to="/settings/integrations" />;
	}

	const users = query.data?.apiUsers ?? [];

	return (
		<Container py={4}>
			<Flex mb={3} alignItems="center">
				<Heading flexGrow={1}>{t('settings.connectors.graphqlApi.title')}</Heading>
				<Button
					small
					type="primary"
					title={t('settings.connectors.graphqlApi.create')}
					loading={creating}
					disabled={creating}
					onClick={() =>
						run(async () => {
							const { data } = await create({ refetchQueries: ['ApiUsers'] });
							if (data) {
								setCredentials({
									apiKey: data.createApiUser.user.apiKey,
									apiSecret: data.createApiUser.apiSecret,
								});
							}
						})
					}
				/>
			</Flex>

			<Paragraph mb={3}>{t('settings.connectors.graphqlApi.description')}</Paragraph>
			<Separator mb={4} />

			{error && (
				<Box mb={3}>
					<GraphQLErrorMessage error={error} />
				</Box>
			)}

			{credentials && (
				<Callout color="green" mb={4}>
					<Box flexGrow={1}>
						<Heading3 mb={2}>{t('settings.connectors.graphqlApi.credentials.title')}</Heading3>
						<Paragraph mb={3}>
							{t('settings.connectors.graphqlApi.credentials.description')}
						</Paragraph>
						<CredentialField
							label={t('settings.connectors.graphqlApi.apiKey')}
							value={credentials.apiKey}
						/>
						<CredentialField
							label={t('settings.connectors.graphqlApi.apiSecret')}
							value={credentials.apiSecret}
						/>
						<Button
							small
							type="secondary"
							title={t('settings.connectors.graphqlApi.credentials.dismiss')}
							onClick={() => setCredentials(undefined)}
						/>
					</Box>
				</Callout>
			)}

			{query.loading && !query.data ? (
				<Spinner centered />
			) : users.length === 0 ? (
				<Paragraph color="grey">{t('settings.connectors.graphqlApi.empty')}</Paragraph>
			) : (
				users.map((user) => (
					<ApiUserCard
						key={user.id}
						user={user}
						onRegenerate={() =>
							run(async () => {
								const { data } = await regenerate({ variables: { id: user.id } });
								if (data) {
									setCredentials({
										apiKey: data.regenerateApiUserSecret.user.apiKey,
										apiSecret: data.regenerateApiUserSecret.apiSecret,
									});
								}
							})
						}
						onArchive={() =>
							run(async () => {
								await archive({ variables: { id: user.id }, refetchQueries: ['ApiUsers'] });
								setCredentials(undefined);
							})
						}
						onChangePermissions={(permissions) =>
							run(async () => {
								await updatePermissions({
									variables: { id: user.id, permissions },
									optimisticResponse: {
										updateApiUserPermissions: { ...user, permissions },
									},
								});
								addToast(t('common.saved'), { appearance: 'success', autoDismiss: true });
							})
						}
					/>
				))
			)}
		</Container>
	);
}

function CredentialField(props: { label: string; value: string }) {
	return (
		<Box mb={3}>
			<Label mb={1}>{props.label}</Label>
			<Flex alignItems="center">
				<Input value={props.value} onChange={() => undefined} inputProps={{ readOnly: true }} />
				<ClipboardButton text={props.value} ml={2} />
			</Flex>
		</Box>
	);
}

function ApiUserCard(props: {
	user: graphql.ApiUserFragment;
	onRegenerate: () => void;
	onArchive: () => void;
	onChangePermissions: (permissions: string[]) => void;
}) {
	const { t } = useTranslation();
	const { user } = props;
	const [confirmArchive, setConfirmArchive] = useState(false);

	return (
		<Card mb={3} p={4}>
			<Heading mb={1}>{user.apiKey}</Heading>
			<Paragraph small color="grey">
				{t('settings.connectors.graphqlApi.user.createdAt', {
					date: new Date(user.createdAt).toLocaleString(),
				})}
				{user.secretCreatedAt &&
					` · ${t('settings.connectors.graphqlApi.user.secretCreatedAt', {
						date: new Date(user.secretCreatedAt).toLocaleString(),
					})}`}
			</Paragraph>
			<Flex mt={2} mb={3}>
				<Button
					small
					type="secondary"
					title={t('settings.connectors.graphqlApi.user.regenerate')}
					onClick={props.onRegenerate}
				/>
				<Button
					small
					ml={2}
					type="destructive"
					icon={<ArchiveIcon />}
					title={t('settings.connectors.graphqlApi.user.delete.request')}
					onClick={() => setConfirmArchive(true)}
				/>
			</Flex>
			{confirmArchive && (
				<Callout color="red" mb={3}>
					<Box flexGrow={1}>
						<Heading3 mb={2}>
							{t('settings.connectors.graphqlApi.user.delete.confirmTitle')}
						</Heading3>
						<Paragraph mb={3}>
							{t('settings.connectors.graphqlApi.user.delete.confirmDescription')}
						</Paragraph>
						<Flex>
							<Box mr={2}>
								<Button
									small
									type="destructive"
									icon={<ArchiveIcon />}
									title={t('settings.connectors.graphqlApi.user.delete.confirm')}
									onClick={props.onArchive}
								/>
							</Box>
							<Button
								small
								type="secondary"
								title={t('common.cancel')}
								onClick={() => setConfirmArchive(false)}
							/>
						</Flex>
					</Box>
				</Callout>
			)}

			<Heading3 mb={1}>{t('settings.connectors.graphqlApi.permissions.title')}</Heading3>
			<Paragraph small color="grey" mb={2}>
				{t('settings.connectors.graphqlApi.permissions.description', {
					selected: user.permissions.length,
				})}
			</Paragraph>
			<PermissionsTree permissions={user.permissions} onChange={props.onChangePermissions} />
		</Card>
	);
}

export function GraphqlApiTile(props: { selected: boolean }) {
	const { t } = useTranslation();

	return (
		<ProductTile
			id="graphql-api"
			title={t('settings.connectors.graphqlApi.title')}
			description={t('settings.connectors.graphqlApi.tile.description')}
			logoImageURL={LOGO_URL}
			selected={props.selected}
		/>
	);
}
